/**
 * Global enrichment-job tracker.
 *
 * A bulk "find emails" run is enqueued as a BULK_ENRICHMENT job and executed by
 * the outreach-bulk-enrich async worker. The SPA used to poll it from inside the
 * component that started it (CsvAudienceUpload / AudiencePicker) — so navigating
 * away killed the poll and the user lost all feedback.
 *
 * This module owns the poll loop OUTSIDE any component: one poll per active job,
 * a global progress toast (react-toastify, rendered by the root <ToastContainer/>
 * so it survives route changes), and a localStorage record so an in-flight job
 * re-attaches after a full page reload. Components call `trackEnrichmentJob` when
 * they start a run and use `useEnrichmentJob` to mirror status inline off the
 * SAME shared poll (no double-polling).
 *
 * Safeguards (a background poller must never leak): the poll gives up after
 * MAX_POLL_ERRORS consecutive failures (a purged/cross-org/dead jobId 404s) or
 * MAX_POLL_MS wall-clock (a server-side-stuck job), and resume skips records
 * older than RESUME_MAX_AGE_MS — so a dead job can never poll forever or
 * resurrect itself on every reload.
 */

import { useEffect, useState } from 'react';
import { enrichmentApi } from './outreach.api';
import { isEnrichmentJobSettled, type SettledEnrichmentJobStatus } from './enrichmentJobStatus';
import type { EnrichmentJobStatus } from '../types';
import { showEnrichmentToast, updateEnrichmentProgress, showEnrichmentDoneToast } from '@/services/toastService';

type Meta = { label?: string; maxAgeMs?: number };
type Listener = (status: EnrichmentJobStatus) => void;
type Persisted = { label?: string; startedAt: number; maxAgeMs?: number };

const STORAGE_KEY = 'outreach:enrichmentJobs:v1';
const POLL_MS = 1500;
const FIRST_POLL_MS = 400;
// Give-up thresholds — a dead/purged/cross-org jobId 404s forever otherwise.
const MAX_POLL_ERRORS = 8; // ~12s of consecutive failures
const MAX_POLL_MS = 30 * 60 * 1000; // absolute cap for a server-side-stuck job
const RESUME_MAX_AGE_MS = 60 * 60 * 1000; // don't resurrect ancient stored jobs

function maxAgeOf(meta: Meta | Persisted): number {
    const requested = Number(meta.maxAgeMs);
    return Number.isFinite(requested) && requested > 0 ? requested : MAX_POLL_MS;
}

type Entry = {
    jobId: string;
    meta: Meta;
    showToast: boolean;
    toastId: ReturnType<typeof showEnrichmentToast> | null;
    timer: ReturnType<typeof setTimeout> | null;
    last: EnrichmentJobStatus | null;
    listeners: Set<Listener>;
    stopped: boolean;
    errorCount: number;
    createdAt: number;
};

const entries = new Map<string, Entry>();

/* ── localStorage durability (survive a full page reload) ── */

function loadStore(): Record<string, Persisted> {
    try {
        const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
}

function writeStore(map: Record<string, Persisted>) {
    try {
        if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    } catch {
        /* private mode / quota — durability is best-effort */
    }
}

function persist(jobId: string, rec: Persisted) {
    const map = loadStore();
    map[jobId] = rec;
    writeStore(map);
}

function unpersist(jobId: string) {
    const map = loadStore();
    if (jobId in map) {
        delete map[jobId];
        writeStore(map);
    }
}

/* ── poll loop ── */

function notify(entry: Entry, status: EnrichmentJobStatus) {
    entry.last = status;
    entry.listeners.forEach((l) => {
        try {
            l(status);
        } catch {
            /* a listener throwing must not break the poll */
        }
    });
}

/** Stop a job: clear its timer, drop the localStorage record, forget the entry. */
function finish(entry: Entry) {
    entry.stopped = true;
    if (entry.timer) clearTimeout(entry.timer);
    entry.timer = null;
    unpersist(entry.jobId);
    entries.delete(entry.jobId);
}

/** Settle the toast (if one was shown) into its terminal state. */
function settleToast(
    entry: Entry,
    status: SettledEnrichmentJobStatus,
    error?: string | null
) {
    if (entry.toastId === null) return;
    showEnrichmentDoneToast(entry.toastId, {
        status,
        found: entry.last?.found ?? 0,
        notFound: entry.last?.notFound ?? 0,
        failed: entry.last?.failed ?? 0,
        total: entry.last?.total ?? 0,
        remaining: entry.last?.remaining ?? 0,
        warning: entry.last?.warning ?? null,
        error,
    });
}

async function poll(entry: Entry) {
    if (entry.stopped) return;
    try {
        const { data } = await enrichmentApi.bulkStatus(entry.jobId);
        entry.errorCount = 0; // a success resets the failure streak
        // Create the toast lazily on the first SUCCESSFUL poll: it never flashes
        // for a dead (404-only) job, and it is created against a settled
        // <ToastContainer/> — robust to StrictMode's boot double-mount tearing
        // the container down while an eager toast would have been orphaned.
        if (entry.showToast && entry.toastId === null) entry.toastId = showEnrichmentToast(entry.meta.label);
        notify(entry, data);
        if (isEnrichmentJobSettled(data.status)) {
            settleToast(entry, data.status, data.error);
            finish(entry);
            return;
        }
        if (entry.toastId !== null) updateEnrichmentProgress(entry.toastId, data, entry.meta.label);
    } catch {
        // Transient failure OR a permanently-dead jobId (purged / cross-org →
        // 404). Retry a bounded number of times, then give up so a dead job can
        // never poll (or resurrect) forever.
        entry.errorCount += 1;
        if (entry.errorCount >= MAX_POLL_ERRORS) {
            settleToast(entry, 'failed', 'lost connection to the run');
            finish(entry);
            return;
        }
    }
    // Absolute wall-clock cap for a job wedged non-terminal server-side.
    if (Date.now() - entry.createdAt >= maxAgeOf(entry.meta)) {
        settleToast(entry, 'failed', 'this is taking longer than expected');
        finish(entry);
        return;
    }
    if (!entry.stopped) entry.timer = setTimeout(() => poll(entry), POLL_MS);
}

/* ── public API ── */

/**
 * Begin tracking a bulk enrichment job globally: starts the shared poll, opens a
 * progress toast on the first successful poll, and persists it so a reload
 * re-attaches. Idempotent — calling again for the same jobId just refreshes its
 * label.
 *
 * @param showToast set false to poll + persist without a toast.
 */
export function trackEnrichmentJob(jobId: string, meta: Meta = {}, showToast = true): void {
    if (!jobId) return;
    const existing = entries.get(jobId);
    if (existing) {
        existing.meta = { ...existing.meta, ...meta };
        return;
    }
    const now = Date.now();
    const entry: Entry = {
        jobId,
        meta,
        showToast,
        toastId: null,
        timer: null,
        last: null,
        listeners: new Set(),
        stopped: false,
        errorCount: 0,
        createdAt: now,
    };
    entries.set(jobId, entry);
    persist(jobId, { label: meta.label, startedAt: now, maxAgeMs: meta.maxAgeMs });
    entry.timer = setTimeout(() => poll(entry), FIRST_POLL_MS);
}

/**
 * Subscribe to live status for a tracked job (for inline display). Returns an
 * unsubscribe fn; unsubscribing only detaches the listener — the global poll +
 * toast keep running. Immediately replays the last-known status if present.
 */
export function subscribeEnrichmentJob(jobId: string, cb: Listener): () => void {
    const entry = entries.get(jobId);
    if (!entry) {
        // A small job can settle between trackEnrichmentJob() and the React
        // effect that subscribes to it. finish() has already removed the entry
        // by then, so recover one authoritative snapshot instead of leaving the
        // component on its initial queued/running state forever.
        let cancelled = false;
        void enrichmentApi
            .bulkStatus(jobId)
            .then(({ data }) => {
                if (cancelled) return;
                try {
                    cb(data);
                } catch {
                    /* listener failures must remain isolated from the tracker */
                }
            })
            .catch(() => {
                /* the shared tracker already owns retries when an entry exists */
            });
        return () => {
            cancelled = true;
        };
    }
    entry.listeners.add(cb);
    if (entry.last) cb(entry.last);
    return () => {
        entry.listeners.delete(cb);
    };
}

/** Re-attach the toast + poll for any job still in flight after a page reload.
 *  Skips (and prunes) records older than RESUME_MAX_AGE_MS so a stale or
 *  cross-org jobId can't resurrect a doomed poll. Call once at app start. */
export function resumeEnrichmentJobsFromStorage(): void {
    const map = loadStore();
    const now = Date.now();
    let mutated = false;
    for (const [jobId, rec] of Object.entries(map)) {
        const startedAt = rec?.startedAt ?? 0;
        const resumeMaxAge = Math.max(RESUME_MAX_AGE_MS, maxAgeOf(rec));
        if (!startedAt || now - startedAt > resumeMaxAge) {
            delete map[jobId];
            mutated = true;
            continue;
        }
        trackEnrichmentJob(jobId, { label: rec.label, maxAgeMs: rec.maxAgeMs });
    }
    if (mutated) writeStore(map);
}

/**
 * React hook: mirror a tracked job's live status inline. Returns null when there
 * is no active job for `jobId`. The poll is owned by the tracker, so this hook
 * unmounting (navigating away) never stops the run.
 */
export function useEnrichmentJob(jobId?: string | null): EnrichmentJobStatus | null {
    const [status, setStatus] = useState<EnrichmentJobStatus | null>(null);
    useEffect(() => {
        if (!jobId) {
            setStatus(null);
            return;
        }
        const unsub = subscribeEnrichmentJob(jobId, setStatus);
        return unsub;
    }, [jobId]);
    return status;
}
